import React from 'react';
import { Box, Grid, Paper, Typography, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

const Card = styled(Paper)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(2),
  boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 360
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  marginBottom: theme.spacing(2)
}));

const LegendDot = styled('span')(({ theme, color }) => ({
  display: 'inline-block',
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: color,
  marginRight: 8
}));

const InlineLegend = ({ items, labelKey, colorKey }) => (
  <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
    {items.map((it, idx) => (
      <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
        <LegendDot color={it[colorKey]} />
        <Typography variant="caption" color="text.secondary">{it[labelKey]}</Typography>
      </Box>
    ))}
  </Stack>
);

// Abbreviate and angle X-axis labels for compact cards
const ClientSalesAxisTick = (props) => {
  const { x, y, payload } = props;
  const mapLabel = (val) => {
    if (!val) return '';
    if (val.toLowerCase() === 'family account') return 'Family Acc.';
    if (val.toLowerCase() === 'sales team') return 'Sales Team';
    return val;
  };
  const label = mapLabel(payload?.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="end" fill="#6b7280" fontSize={11} transform="rotate(-25)">
        {label}
      </text>
    </g>
  );
};

const DashboardChartsRow = ({
  clientCategoryBreakdown,
  companyClientProfileStrength,
  clientSalesData,
  taskManager,
  packAppearance,
  profileStatus,
  support,
  planBreakdown,
  totalClients
}) => {
  const profileStrengthData = [
    { name: 'Gap', value: companyClientProfileStrength?.gap || 0, color: '#0b91d6' },
    { name: 'Strength', value: companyClientProfileStrength?.strength || 0, color: '#34a853' },
    { name: 'Archived', value: companyClientProfileStrength?.archived || 0, color: '#ef232a' }
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        {/* Row 1 */}
        <Grid item xs={12} md={4}>
          <Card>
            <SectionTitle>Client Category Breakdown</SectionTitle>
            {/* Plan breakdown will be shown as an inner pie; total clients displayed in KPI section above, so omit duplicate card here */}
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  {(() => {
                    const hasPlanCounts = planBreakdown && (Array.isArray(planBreakdown) ? planBreakdown.length > 0 : Object.keys(planBreakdown || {}).length > 0);
                    if (hasPlanCounts) {
                      // Render plan distribution (Basic / Advance / Pro) as primary pie
                      let pbArr = [];
                      if (Array.isArray(planBreakdown)) {
                        pbArr = planBreakdown.map(it => ({ key: (it.category || it.label || it.type || '').toString().toLowerCase(), label: it.category || it.label || it.type || 'Plan', count: it.count || it.value || 0, color: it.color || '#90A4AE' }));
                      } else {
                        const mapOrder = ['basic', 'advance', 'pro'];
                        const colorMap = { pro: '#FFB74D', advance: '#4CAF50', basic: '#7CB9E8' };
                        pbArr = mapOrder.map(k => ({ key: k, label: k.charAt(0).toUpperCase() + k.slice(1), count: (planBreakdown[k] || 0), color: colorMap[k] || '#90A4AE' }));
                      }
                      const totalPB = pbArr.reduce((s, p) => s + (p.count || 0), 0);
                      const normalizedPB = pbArr.map(p => ({ category: p.label, percentage: totalPB > 0 ? Math.round(((p.count || 0) / totalPB) * 100) : 0, color: p.color, rawCount: p.count || 0 }));
                      return (
                        <Pie data={normalizedPB} dataKey="percentage" nameKey="category" outerRadius={70} labelLine={false}>
                          {normalizedPB.map((entry, index) => (
                            <Cell key={`cell-ccb-${index}`} fill={entry.color || '#90A4AE'} />
                          ))}
                        </Pie>
                      );
                    }

                    // Fallback to client category breakdown
                    if (clientCategoryBreakdown && clientCategoryBreakdown.length > 0) {
                      const totalFromCounts = clientCategoryBreakdown.reduce((s, it) => s + (it.count || 0), 0);
                      const normalized = clientCategoryBreakdown.map((it) => {
                        const pct = (typeof it.percentage === 'number' && !Number.isNaN(it.percentage))
                          ? it.percentage
                          : (totalFromCounts > 0 ? Math.round(((it.count || 0) / totalFromCounts) * 100) : 0);
                        return {
                          category: it.category || it.label || 'Unknown',
                          percentage: pct,
                          color: it.color || '#90A4AE'
                        };
                      });
                      return (
                        <Pie data={normalized} dataKey="percentage" nameKey="category" outerRadius={70} labelLine={false}>
                          {normalized.map((entry, index) => (
                            <Cell key={`cell-ccb-${index}`} fill={entry.color || '#90A4AE'} />
                          ))}
                        </Pie>
                      );
                    }

                    // Empty state
                    return (
                      <Pie data={[{ category: 'No Data', percentage: 100, color: '#E0E7FF' }]} dataKey="percentage" nameKey="category" outerRadius={70} labelLine={false}>
                        <Cell key={`cell-ccb-empty`} fill="#E0E7FF" />
                      </Pie>
                    );
                  })()}
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <InlineLegend items={(planBreakdown && (Array.isArray(planBreakdown) ? planBreakdown.length > 0 : Object.keys(planBreakdown || {}).length > 0)) ? (Array.isArray(planBreakdown) ? planBreakdown.map(it => ({ category: it.category || it.label || it.type || 'Plan', color: it.color || '#90A4AE' })) : ['basic','advance','pro'].map(k => ({ category: k.charAt(0).toUpperCase()+k.slice(1), color: k === 'pro' ? '#FFB74D' : k === 'advance' ? '#4CAF50' : '#7CB9E8' }))) : ((clientCategoryBreakdown && clientCategoryBreakdown.length > 0) ? clientCategoryBreakdown.map(it => ({ category: it.category || it.label || 'Unknown', color: it.color || '#90A4AE' })) : [{ category: 'No Data', color: '#E0E7FF' }])} labelKey="category" colorKey="color" />
            {/* Fallback: If planBreakdown is an object of counts, show raw numbers for clarity */}
            {planBreakdown && !Array.isArray(planBreakdown) && typeof planBreakdown === 'object' && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {['pro', 'advance', 'basic', 'none'].map((k) => (
                  <Box key={k} sx={{ bgcolor: '#fff', px: 1.25, py: 0.5, borderRadius: 1, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
                    <Typography sx={{ fontSize: 12, color: '#6b7280', textTransform: 'capitalize' }}>{k}</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{planBreakdown[k] || 0}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <SectionTitle>Company Client Profile Strength</SectionTitle>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={profileStrengthData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} labelLine={false}>
                    {profileStrengthData.map((entry, index) => (
                      <Cell key={`cell-ps-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <InlineLegend items={profileStrengthData} labelKey="name" colorKey="color" />
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <SectionTitle>Client Sales Data</SectionTitle>
            <Box sx={{ height: 310,width:490 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={clientSalesData} margin={{ bottom: 38, left: 0, right: 10, top: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="source" interval={0} tick={<ClientSalesAxisTick />} height={34} />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="value" barSize={20} radius={[4,4,0,0]}>
                    {clientSalesData.map((entry, index) => (
                      <Cell key={`cell-csd-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        {/* Row 2 */}
        <Grid item xs={12} md={3}>
          <Card>
            <SectionTitle>Task Manager</SectionTitle>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={taskManager} dataKey="percentage" nameKey="status" outerRadius={70} labelLine={false}>
                    {taskManager.map((entry, index) => (
                      <Cell key={`cell-tm-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <InlineLegend items={taskManager} labelKey="status" colorKey="color" />
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <SectionTitle>Pack Appearance</SectionTitle>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={packAppearance} dataKey="percentage" nameKey="type" outerRadius={70} labelLine={false}>
                    {packAppearance.map((entry, index) => (
                      <Cell key={`cell-pa-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <InlineLegend items={packAppearance} labelKey="type" colorKey="color" />
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <SectionTitle>Profile status</SectionTitle>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={profileStatus} dataKey="percentage" nameKey="status" outerRadius={70} labelLine={false}>
                    {profileStatus.map((entry, index) => (
                      <Cell key={`cell-prs-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <InlineLegend items={profileStatus} labelKey="status" colorKey="color" />
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <SectionTitle>Support</SectionTitle>
            <Box sx={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={support} dataKey="count" nameKey="status" outerRadius={70} labelLine={false}>
                    {support.map((entry, index) => (
                      <Cell key={`cell-sup-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <InlineLegend items={support} labelKey="status" colorKey="color" />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardChartsRow;
